import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Menu,
	MenuItem,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useTheme,
} from "@mui/material";
import React, {ChangeEvent, useEffect, useRef, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {useDialogs} from "@toolpad/core";
import {useSnackbar} from "notistack";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import ServerSideDataTable from "@components/ServerSideDataTable";
import {urls} from "@routes";
import {Link} from "react-router-dom";
import {CustomCellRendererProps} from "ag-grid-react";
import IconButton from "@mui/material/IconButton";
import {Add, Archive, Cancel, ContentCopy, MoreVert, OpenInNew, Refresh, Update} from "@mui/icons-material";
import {format} from "date-fns";
import {formatInTimeZone} from "date-fns-tz";

interface PageData {
	status_code: number
	status_message: string

	campaigns: Campaign[]
	archived_campaigns: Campaign[]
	user_id: number
	timezone: string
	contact_lists: []
}

interface Campaign {
	uid: string
	name: string
	subdomain: string
	created_on_ts: number
	domains: Array<string>
	status: string
	total_emails_sent: number
	total_replies_received: number
	contacts_count: number
}

interface WebhookMessage {
	event_type: string
	event_data: any
}

interface ContactList {
	uid: string
	name: string
	created_on: number
	total_contacts: number
	total_columns: number
}

interface ContactListQueryResult {
	status_code: string
	status_text: string

	contact_lists: ContactList[]
}

type StatusFilter = "all" | "ongoing" | "completed" | "cancelled" | "archived";

export default function Campaigns() {
	const dialogs = useDialogs();
	const {enqueueSnackbar} = useSnackbar();
	const ws = useRef<WebSocket | null>(null);
	const theme = useTheme();

	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
	const [campaigns, setCampaigns] = useState<Campaign[]>([]);
	const [archivedCampaigns, setArchivedCampaigns] = useState<Campaign[]>([]);
	const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
	const [updateCampaignModal, setUpdateCampaignModal] = useState(false)
	const [updateCampaignUID, setUpdateCampaignUID] = useState("")
	const [campaignName, setCampaignName] = useState("")
	const [contactLists, setContactLists] = useState<ContactList[]>([]);
	const [selectedContactLists, setSelectedContactLists] = useState<ContactList[]>([]);
	const [userId, setUserId] = useState<number>();
	const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
	const [duplicateTargetCampUID, setDuplicateTargetCampUID] = useState<string>("");
	const [timezone, setTimezone] = useState("")

	// Page Title
	useEffect(() => {
		document.title = "All Campaigns - Deliveryman.ai";
	}, []);

	// Websocket code
	useEffect(() => {
		const wsUrl = `${process.env.REACT_APP_WS_HOST}/ws/campaigns/${userId}/`;

		// Initialize WebSocket connection
		if (userId) {
			ws.current = new WebSocket(wsUrl);

			ws.current.onopen = () => {
				console.log("WebSocket connection established");
			};

			ws.current.onmessage = (event) => {
				const data: WebhookMessage = JSON.parse(event.data)["event_data"];
				const eventName: string = data["event_name"]
				const eventData: any = data["event_data"]

				if (eventName === "campaign_status_update") {
					const campaignUID: string = eventData["campaign_uid"]
					const newStatus: string = eventData["new_status"]
					setCampaigns(prevItems => prevItems.map(campaign => campaign.uid === campaignUID ? {
						...campaign,
						status: newStatus
					} : campaign));
				} else {
					console.log("unhandled ws event:", event);
				}
			};

			ws.current.onclose = () => {
				console.log("WebSocket connection closed");
			};

			ws.current.onerror = (error) => {
				console.error("WebSocket error:", error);
			};
		}

		// Cleanup on component unmount
		return () => {
			if (ws.current) {
				console.log("Cleaning up websocket for closing...");
				ws.current.close();
			}
		};
	}, [userId]);

	// Fetch campaigns data.
	const pageDataQuery = useQuery({
		queryKey: ["campaignPageData"],
		queryFn: () => authenticateAndFetchData("/campaigns/get"),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});
	useEffect(() => {
		if (pageDataQuery.data) {
			let data = pageDataQuery.data.data as PageData;
			setCampaigns(data.campaigns);
			setContactLists(data.contact_lists)
			setArchivedCampaigns(data.archived_campaigns);
			setUserId(data.user_id);
			setTimezone(data.timezone)
		}
	}, [pageDataQuery.data]);

	const contactListsQuery = useQuery({
		queryKey: ["contactListsQuery"],
		queryFn: () => authenticateAndFetchData("/campaigns/create/fetch-contact-lists/"),
		gcTime: 0,
		retry: retryFn,
		enabled: false,
		refetchOnWindowFocus: false,
	});
	useEffect(() => {
		if (contactListsQuery.data) {
			let data = contactListsQuery.data.data as ContactListQueryResult
			setContactLists(data.contact_lists);
		}
	}, [contactListsQuery.data]);

	// Update the filtered campaign data when main campaign data changes or the filter changes.
	useEffect(() => {
		if (statusFilter === "all") {
			setFilteredCampaigns(campaigns);

		} else if (statusFilter === "ongoing") {
			setFilteredCampaigns(campaigns.filter(camp => [
				"created", "running", "scheduled"
			].includes(camp.status)));

		} else if (statusFilter === "completed") {
			setFilteredCampaigns(campaigns.filter(camp => camp.status === "complete"));

		} else if (statusFilter === "cancelled") {
			setFilteredCampaigns(campaigns.filter(camp => camp.status === "cancelled"));

		} else {
			// Archived
			setFilteredCampaigns(archivedCampaigns);
		}
	}, [campaigns, archivedCampaigns, statusFilter]);


	const updateCampaignNameMutation = useMutation({
		mutationKey: ["updateCampaignName"],
		mutationFn: ({uid, name}: { uid: string; name: string }) =>
			authenticateAndPostData("/campaigns/update-campaign-name/", {
				campaign_id: uid,
				name: name,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (_, variables) => {
			setCampaigns((prev) =>
				prev.map((list) =>
					list.uid === variables.uid ? {...list, name: variables.name} : list
				)
			);
			enqueueSnackbar("campaign name renamed successfully!", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		},
	});

	// Mutation to cancel campaign.
	const cancelCampaignMutation = useMutation({
		mutationKey: ["cancelCampaign"],
		mutationFn: (campaignUID: string) => authenticateAndPostData("/campaigns/cancel-campaign/", {
			campaign_uid: campaignUID,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (response) => {
			let uid: string = response.data["campaign_uid"]
			campaigns.map(campaign => {
				if (campaign.uid === uid) campaign.status = "cancelled";
				return campaign;
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to archive campaign.
	const archiveCampaignMutation = useMutation({
		mutationKey: ["archiveCampaign"],
		mutationFn: (campaignUID: string) => authenticateAndPostData("/campaigns/archive-campaign/", {
			campaign_uid: campaignUID,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			pageDataQuery.refetch().then();
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	function campaignStatusChipColor(status: string) {
		switch (status) {
			case "creating":
				return "warning";

			case "created":
				return "default";

			case "scheduled":
				return "primary";

			case "running":
				return "primary";

			case "complete":
				return "success";

			case "cancelled":
				return "error";

			case "paused":
				return "warning"

			default:
				return "default";
		}
	}

	function campaignCancelDisabled(campaignStatus: string) {
		return !["created", "running", "paused"].includes(campaignStatus)
	}

	function timestampFormatter(params: ValueFormatterParams, timezone: string) {
		if (!params.value) return "";

		return formatInTimeZone(
			new Date(params.value),
			timezone,
			"hh:mm a, do MMM yyyy"
		).replace(/\b(AM|PM)\b/, (match) => match.toLowerCase());
	}

	const CampaignLink = (params: CustomCellRendererProps) => {
		if (params.data) {
			if (params.data["status"] !== "creating") {
				return (
					<Link style={{color: theme.palette.text.primary}}
								to={urls["campaignEmailDetails"].replace(":campaignUID", params.data["uid"])}>
						{params.value}
					</Link>
				)

			} else {
				return (
					<Typography>
						{params.value}
					</Typography>
				)
			}

		} else {
			<Typography>{params.value}</Typography>
		}
	}

	const StatusBadge = (params: CustomCellRendererProps) => {
		return (
			<Chip size={"small"} color={campaignStatusChipColor(params.value)} label={params.value}/>
		)
	}

	const TableTopRow = (props: {
		value: StatusFilter,
		onChange: (event: React.MouseEvent<HTMLElement>, newValue: string | null) => void,
	}) => {
		return (
			<Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} sx={{width: "100%", pl: 4}}>
				<ToggleButtonGroup
					color={"primary"}
					value={props.value}
					exclusive
					onChange={props.onChange}
				>
					<ToggleButton size={"small"} value="all">
						All
					</ToggleButton>
					<ToggleButton size={"small"} value="ongoing">
						Ongoing
					</ToggleButton>
					<ToggleButton size={"small"} value="completed">
						Completed
					</ToggleButton>
					<ToggleButton size={"small"} value="cancelled">
						Cancelled
					</ToggleButton>
					<ToggleButton size={"small"} value="archived">
						Archived
					</ToggleButton>
				</ToggleButtonGroup>

				<Button component={Link}
								to={urls["createCampaign"]}
								startIcon={<Add/>}
								variant="contained"
								size={"small"}
								color="primary">
					Create New Campaign
				</Button>
			</Box>
		)
	}

	function contactTimestampFormatter(params: ValueFormatterParams) {
		return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
	}

	const ContactsPreviewButton = (customProps: CustomCellRendererProps) => {
		return (
			<Button component={Link}
							to={urls["contactListDetails"].replace(":contactListUID", customProps.data["uid"])}
							target={"_blank"}
							rel={"noreferrer"}
							size={"small"}
							startIcon={<OpenInNew/>}>
				Preview
			</Button>
		)
	}

	const contactListSelectionTableColumnDefs: ColDef[] = [
		{field: "name", headerName: "Name"},
		{field: "created_on", headerName: "Imported On", valueFormatter: contactTimestampFormatter, flex: 2},
		{field: "total_contacts", headerName: "Total Contacts"},
		{field: "total_columns", headerName: "Total Columns"},
		{cellRenderer: ContactsPreviewButton, resizable: false, sortable: false}
	]

	// Table action buttons.
	const ContactListTableActions = () => (
		<Stack direction={"row"} spacing={2}>
			<Button startIcon={<Refresh/>}
							variant={"contained"}
							size={"small"}
							disabled={contactListsQuery.isFetching || pageDataQuery.isFetching}
							onClick={() => contactListsQuery.refetch().then()}>
				{contactListsQuery.isFetching ? "Loading..." : "Refresh"}
			</Button>
			<Button component={Link}
							to={urls["contactLists"] + "?newimport=1"}
							target={"_blank"}
							rel={"noreferrer"}
							startIcon={<OpenInNew/>}
							variant={"contained"}
							size={"small"}>
				Add New Contact List
			</Button>
		</Stack>
	)

	function refetchPageData() {
		pageDataQuery.refetch().then();
	}

	const ActionsButton = (props: CustomCellRendererProps) => {
		const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
		const open = Boolean(anchorEl);

		const handleClick = (event: any) => {
			setAnchorEl(event.currentTarget);
		};

		const handleClose = () => {
			setAnchorEl(null);
		};

		const handleDuplicate = () => {
			setDuplicateTargetCampUID(props.data["uid"]);
			setOpenDuplicateModal(true);
			handleClose();
		};

		const handleEditName = () => {
			setUpdateCampaignUID(props.data["uid"]);
			setCampaignName(props.data['name'])
			setUpdateCampaignModal(true);
			handleClose();
		};

		const handleArchive = async () => {
			const archiveConfirmed = await dialogs.confirm(
				`Archiving will remove it from the campaigns table without actually deleting it. This action cannot be undone.`,
				{
					title: "Archive this campaign?",
					cancelText: "No",
					okText: "Yes",
				}
			);
			if (archiveConfirmed) {
				archiveCampaignMutation.mutate(props.data["uid"]);
			}
		}

		const handleDelete = async () => {
			const cancelConfirmed = await dialogs.confirm(
				`Are you sure you want to cancel campaign "${props.data['name']}"? All 
				remaining schedules will be deleted but you can still access campaign data.`,
				{
					title: "Cancel Campaign",
					cancelText: "Cancel",
					okText: "Confirm",
				}
			);
			if (cancelConfirmed) {
				cancelCampaignMutation.mutate(props.data["uid"]);
			}
			handleClose();
		};

		return (
			<>
				<IconButton
					aria-label="more"
					aria-controls={open ? 'long-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-haspopup="true"
					onClick={handleClick}
				>
					<MoreVert/>
				</IconButton>
				<Menu
					id="long-menu"
					MenuListProps={{
						'aria-labelledby': 'long-button',
					}}
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
				>
					<MenuItem onClick={handleDuplicate} disabled={!["complete", "cancelled"].includes(props.data["status"])}>
						<ContentCopy color={"primary"} sx={{mr: 1}}/>
						<Typography variant="subtitle2">Duplicate</Typography>
					</MenuItem>
					{statusFilter !== "archived" &&
              <MenuItem onClick={handleArchive} disabled={!["complete", "cancelled"].includes(props.data["status"])}>
                  <Archive color={"warning"} sx={{mr: 1}}/>
                  <Typography variant="subtitle2">Archive</Typography>
              </MenuItem>}
					{statusFilter !== "archived" &&
              <MenuItem onClick={handleDelete} disabled={campaignCancelDisabled(props.data["status"])}>
                  <Cancel color={"error"} sx={{mr: 1}}/>
                  <Typography variant="subtitle2">Cancel Campaign</Typography>
              </MenuItem>}
					<MenuItem onClick={handleEditName}>
						<Update color="error" sx={{mr: 1}}/>
						<Typography variant="subtitle2">Rename Campaign</Typography>
					</MenuItem>
				</Menu>
			</>
		);
	}


	// Columns for campaigns table.
	const columnDefs: ColDef[] = [
		{field: "name", headerName: "Campaign Name", cellRenderer: CampaignLink, flex: 2},
		{field: "uid", headerName: "Campaign UID"},
		{
			field: "created_on_ts",
			headerName: "Created On",
			valueFormatter: (params) => timestampFormatter(params, timezone),
			flex: 2
		},
		{field: "total_emails_sent", headerName: "Emails Sent"},
		{field: "total_replies_received", headerName: "Replies Received"},
		{field: "contacts_count", headerName: "Contacts"},
		{field: "domains", headerName: "Domains", flex: 2},
		{field: "status", headerName: "Status", cellRenderer: StatusBadge},
		{headerName: "Actions", cellRenderer: ActionsButton, sortable: false, resizable: false},
	]

	function handleStatusFilterChange(_event: React.MouseEvent<HTMLElement>, newValue: string | null) {
		if (newValue) {
			setStatusFilter(newValue as StatusFilter);
		}
	}

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (pageDataQuery.isFetching) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else {
		return (
			<Stack direction={"column"} spacing={4} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"column"} spacing={1}>
					<Typography variant={"h4"} align={"center"} color={"primary"}>
						Launch & Manage Campaigns
					</Typography>
				</Stack>
				<Box sx={{height: "100%"}}>
					<ServerSideDataTable columns={columnDefs}
															 rows={filteredCampaigns}
															 actions={<TableTopRow value={statusFilter} onChange={handleStatusFilterChange}/>}
															 noRowsText={"Add a domain before creating a campaign"}/>
				</Box>

				{/* Duplicate campaign modal */}
				<DulicateCampaignModal open={openDuplicateModal}
															 onClose={() => {
																 setDuplicateTargetCampUID("");
																 setOpenDuplicateModal(false);
															 }}
															 duplicateTargetCampUID={duplicateTargetCampUID}
															 refetchPageData={refetchPageData}
															 contactLists={contactLists}
															 setContactLists={setContactLists}
															 selectedContactLists={selectedContactLists}
															 setSelectedContactLists={setSelectedContactLists}
															 contactListSelectionTableColumnDefs={contactListSelectionTableColumnDefs}
															 ContactListTableActions={ContactListTableActions}
				/>
				<UpdateCampaignNameModal open={updateCampaignModal}
																 onClose={() => {
																	 setUpdateCampaignUID("");
																	 setUpdateCampaignModal(false);
																 }}
																 campaignName={campaignName}
																 onConfirm={(name: string) => {
																	 updateCampaignNameMutation.mutate({
																		 uid: updateCampaignUID,
																		 name
																	 });
																 }}/>
			</Stack>
		)
	}
}

function DulicateCampaignModal(props: {
	open: boolean,
	onClose: () => void,
	duplicateTargetCampUID: string,
	refetchPageData: () => void,
	contactLists: ContactList[],
	setContactLists: (lists: ContactList[]) => void,
	selectedContactLists: ContactList[],
	setSelectedContactLists: (lists: ContactList[]) => void,
	contactListSelectionTableColumnDefs: ColDef[],
	ContactListTableActions: () => JSX.Element
}) {
	const theme = useTheme();
	const {enqueueSnackbar} = useSnackbar();
	const [inputValue, setInputValue] = useState('');
	const [emailsPerDay, setEmailsPerDay] = useState<string>("");

	// Mutation to duplicate campaign.
	const duplicateCampaignMutation = useMutation({
		mutationKey: ["duplicateCampaignMutation"],
		mutationFn: (data: { name: string, targetCampaignUID: string, contactLists: string[] }) => authenticateAndPostData(
			"/campaigns/duplicate/", {
				"campaign_name": data.name,
				"target_campaign_uid": data.targetCampaignUID,
				"selected_contact_list_uids": data.contactLists,
				"emails_per_day": emailsPerDay,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			enqueueSnackbar(
				"Campaign has been duplicated successfully!",
				{variant: "success"}
			);
			props.onClose()
			props.refetchPageData();
		},
		onError: (error: ApiRequestFailed) => {
			console.log(error);
			enqueueSnackbar(
				error.data.message || "Something went wrong on our side. Please try again in some time.",
				{variant: "error"}
			);
		}
	});


	const handleConfirm = () => {
		if (!inputValue.trim() || props.selectedContactLists.length === 0) {
			return;
		}
		duplicateCampaignMutation.mutate({
			name: inputValue,
			targetCampaignUID: props.duplicateTargetCampUID,
			contactLists: props.selectedContactLists.map(val => val.uid)
		});

	};

	function emailsPerDayHandler(event: ChangeEvent<HTMLInputElement>) {
		// Only allow numbers.
		const newValue = event.target.value;
		if (newValue === '' || /^[0-9\b]+$/.test(newValue)) {
			setEmailsPerDay(newValue);
		}
	}

	return (
		<Dialog open={props.open} onClose={props.onClose} fullWidth
						maxWidth="lg"
						sx={{
							"& .MuiDialog-paper": {
								width: {
									xs: "95%",
									sm: "85%",
									md: "75%",
									lg: "60%",
								},
								maxWidth: "1200px"
							}
						}}
		>
			<DialogTitle>Duplicate Email Campaign</DialogTitle>
			<DialogContent>
				<Typography variant={"body2"}>
					Duplicating this campaign will copy over all the messages sequences into a new campaign.
				</Typography>
				<TextField
					label="New Campaign Name"
					type="text"
					fullWidth
					value={inputValue}
					sx={{mt: 2}}
					disabled={duplicateCampaignMutation.isPending}
					onChange={(e) => setInputValue(e.target.value)}
				/>
				{/* Emails Per Day */}
				<Stack direction={"column"} spacing={1} sx={{mt: 2, mb: 2}}>
					<Box>
						<Typography variant={"subtitle1"} fontWeight={"bold"}>
							How many leads/contacts should we email per day through this campaign?
						</Typography>
						<Typography variant={"body2"}>
							You can specify a fixed number of emails to be sent per day or you can leave it empty so we can
							automatically handle the best sending limits.
						</Typography>
						{/*<Typography variant={"body2"} sx={{mt: 2}}>*/}
						{/*	(NOTE: Less than provided number of emails might be sent on a day depending on certain factors)*/}
						{/*</Typography>*/}
					</Box>
					<TextField
						type="text"
						value={emailsPerDay}
						onChange={emailsPerDayHandler}
						variant="outlined"
						placeholder={"Auto"}
						slotProps={{
							htmlInput: {
								inputMode: 'numeric',
								pattern: '[0-9]*'
							}
						}}
						sx={{maxWidth: "100px"}}
					/>
				</Stack>
				<Typography variant={"subtitle1"} sx={{fontWeight: "bold", mt: 2, mb: 2}}>
					<span style={{color: theme.palette.error.main}}>*</span>&nbsp;Select at least one contact list to use:
				</Typography>
				<Box sx={{
					height: "500px",
					"& .MuiTextField-root": {width: "200px"},
					"& .MuiButton-root": {
						fontSize: "0.75rem",
						padding: "4px 8px",
						minWidth: "auto"
					}
				}}>
					<ServerSideDataTable columns={props.contactListSelectionTableColumnDefs}
															 rows={props.contactLists}
															 onSelectionChange={(rows) => {
																 props.setSelectedContactLists(rows);
															 }}
															 rowSelection={{mode: "singleRow"}}
															 actions={props.ContactListTableActions()}/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={props.onClose}>Cancel</Button>
				<Button onClick={handleConfirm} color="primary"
								disabled={duplicateCampaignMutation.isPending || !inputValue.trim() || props.selectedContactLists.length === 0}>
					{duplicateCampaignMutation.isPending ? "Creating Duplicate..." : "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}


function UpdateCampaignNameModal(props: {
	open: boolean,
	onClose: () => void,
	onConfirm: (name: string) => void,
	campaignName: string,
}) {
	const [inputValue, setInputValue] = useState('');

	const handleConfirm = () => {
		props.onConfirm(inputValue);
		props.onClose();
	};

	return (
		<Dialog open={props.open} onClose={props.onClose}>
			<DialogTitle>Rename Campaign : {props.campaignName}</DialogTitle>
			<DialogContent>
				<TextField
					label="New Campaign Name"
					type="text"
					fullWidth
					value={inputValue}
					sx={{mt: 2}}
					onChange={(e) => setInputValue(e.target.value)}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={props.onClose}>Cancel</Button>
				<Button onClick={handleConfirm} color="primary">
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
	);
}
